import { useQuery } from "@tanstack/react-query";

import { api as cyranoApi } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";

/**
 * Etat d'onboarding d'un creator — 3 etapes calculees cote backend (Cyrano)
 * a partir des donnees Neon. Pollee toutes les 30s (refetchInterval) ET au
 * focus de l'onglet (refetchOnWindowFocus default true).
 *
 * Source : `GET /api/creators/{creator_id}/onboarding-state`. Cf
 * `app/api/routes/creator_onboarding.py` cote Cyrano.
 *
 * Refonte 2026-05-31 : l'onboarding = création du créateur uniquement
 * (identity → telegram → telegram_group). L'ACTIVATION de Cyrano ne fait
 * plus partie de l'onboarding — elle vit dans l'onglet Créateur → Agent
 * (`AgentTab`, endpoint `cyrano-mode`). L'etape
 * `telegram` est combinee (bot token + compte MTProto). Persona, contenu
 * (medias) et enrichissement ne bloquent pas non plus l'onboarding — ils
 * restent editables hors onboarding (tab Creator + panel Vault).
 *
 * Le cockpit utilise ce hook pour driver le `CreatorOnboardingWizard`
 * (overlay fullscreen qui force l'user à finir la création du creator
 * tant que `is_complete === false`).
 */

export type CreatorOnboardingStepName =
  | "identity"
  | "telegram"
  | "telegram_group"
  | "complete";

export interface CreatorOnboardingState {
  creator_id: string;
  current_step: CreatorOnboardingStepName;
  is_complete: boolean;
  identity: { done: boolean; display_name: string; color: string | null };
  /** Etape combinee "connexion Telegram" : bot token + compte MTProto.
   *  `done = bot_done`. */
  telegram: {
    done: boolean;
    bot_done: boolean;
    bot_username: string | null;
    bot_first_name: string | null;
  };
  telegram_group: {
    done: boolean;
    vault_chat_id: number | null;
    vault_chat_title: string | null;
    vault_verified_at: string | null;
    notification_chat_id: number | null;
    notification_chat_title: string | null;
    notification_enabled: boolean;
  };
}

const DEFAULT_POLL_INTERVAL_MS = 30_000;

/**
 * cyranoCreatorId : UUID Neon du creator (different du Convex Id). Resolvable
 * via `/api/creators` (cf CreatorsPanel.fetchCreatorIdMap). Si null,
 * la query est skippee — utile pendant le mapping slug→UUID.
 *
 * pollIntervalMs : override le poll par défaut (30s). Utile quand l'UI
 * attend un event externe (ex: webhook Telegram detection bot) et a besoin
 * d'un refresh plus rapide.
 */
export function useCreatorOnboardingState(
  cyranoCreatorId: string | null,
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): {
  data: CreatorOnboardingState | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;
  const enabled = isAuthenticated && cyranoCreatorId !== null;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["creator-onboarding-state", cyranoCreatorId],
    queryFn: async (): Promise<CreatorOnboardingState> => {
      return cyranoApi.get<CreatorOnboardingState>(
        `/api/creators/${cyranoCreatorId}/onboarding-state`,
      );
    },
    enabled,
    refetchInterval: pollIntervalMs,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  return { data, isLoading, isError, refetch: () => void refetch() };
}
